import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronDown, Flame, Timer, Clock, Infinity as InfinityIcon, Waves, Loader2, Check } from 'lucide-react';
import { useClientAuth } from '../../../context/ClientAuthContext';

const METHOD_ICONS = { hiit: Flame, tabata: Timer, emom: Clock, amrap: InfinityIcon, intervals: Waves };
const METHOD_COLORS = {
  hiit: 'from-orange-500 to-red-500', tabata: 'from-red-500 to-pink-600',
  emom: 'from-amber-500 to-orange-600', amrap: 'from-violet-500 to-fuchsia-600',
  intervals: 'from-sky-500 to-cyan-500',
};

/** Guide explicatif complet des méthodes cardio (HIIT/Tabata/EMOM/AMRAP/Intervalles) — accordéon. */
export const CardioGuide = ({ en, onBack }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    authFetch('/api/tracking/cardio-pro/methods').then((r) => r.json()).then((d) => setMethods(d.methods || [])).finally(() => setLoading(false));
  }, [authFetch]);

  return (
    <div className="space-y-4" data-testid="cardio-guide">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-guide-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <h2 className="text-xl font-black text-white flex items-center gap-2">
        <Flame className="w-5 h-5 text-orange-400" /> {L('Guide des méthodes Cardio', 'Cardio Methods Guide')}
      </h2>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
      ) : (
        <div className="space-y-2.5">
          {methods.map((m) => {
            const Icon = METHOD_ICONS[m.id] || Flame;
            const open = openId === m.id;
            return (
              <div key={m.id} className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 overflow-hidden" data-testid={`cardio-guide-method-${m.id}`}>
                <button onClick={() => setOpenId(open ? null : m.id)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`cardio-guide-toggle-${m.id}`}>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${METHOD_COLORS[m.id]} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{m.label}</p>
                    <p className="text-xs text-zinc-500">{m.short}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-4 pb-4 pt-1 space-y-3">
                        <p className="text-sm text-zinc-300 leading-relaxed">{m.guide}</p>
                        <ul className="space-y-1.5">
                          {(m.benefits || []).map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                              <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {b}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-zinc-600">
                          {L('Réglages par défaut', 'Default settings')} : {m.default_rounds} {L('tours', 'rounds')} · {m.default_work_seconds}s {L('effort', 'work')} · {m.default_rest_seconds}s {L('repos', 'rest')}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CardioGuide;
